// src/features/services/stock/components/MovementTimeline.tsx
// ─────────────────────────────────────────────────────────────────────────────
// P3.1 — Movement Timeline for a selected SKU.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import type { StockMovement } from '@/features/services/sales/api/stockApi';

interface MovementTimelineProps {
  items: StockMovement[];
  isLoading: boolean;
}

const TYPE_COLORS: Record<string, string> = {
  wo_yield:            'text-emerald-400',
  wo_consume:          'text-amber-400',
  adjustment:          'text-sky-400',
  reservation_commit:  'text-violet-400',
};

export default function MovementTimeline({ items, isLoading }: MovementTimelineProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="pac-glass rounded-lg h-10 animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pac-glass-raised rounded-lg px-4 py-8 text-center text-gray-500">
        No movements recorded yet.
      </div>
    );
  }

  return (
    <div className="pac-glass-raised rounded-lg overflow-hidden">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="border-b border-white/10 text-gray-400 text-xs uppercase tracking-wide">
            <th className="px-4 py-2">Type</th>
            <th className="px-4 py-2 text-right">Delta</th>
            <th className="px-4 py-2 text-right">Balance</th>
            <th className="px-4 py-2">Reference</th>
            <th className="px-4 py-2">Lot</th>
            <th className="px-4 py-2">Notes</th>
            <th className="px-4 py-2">Date</th>
          </tr>
        </thead>
        <tbody>
          {items.map(m => {
            const cls = TYPE_COLORS[m.movement_type] ?? 'text-gray-300';
            const sign = m.quantity_delta > 0 ? '+' : '';
            return (
              <tr key={m.movement_key} className="border-b border-white/5">
                <td className={`px-4 py-2 ${cls}`}>{m.movement_type}</td>
                <td className="px-4 py-2 text-right tabular-nums text-white">
                  {sign}{m.quantity_delta}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-400">
                  {m.balance_after}
                </td>
                <td className="px-4 py-2 text-gray-400 text-xs">
                  {m.reference_key || m.reference_type || '—'}
                </td>
                <td className="px-4 py-2 text-gray-500 text-xs">
                  {m.lot_number || '—'}
                </td>
                <td className="px-4 py-2 text-gray-500 text-xs truncate max-w-[120px]">
                  {m.notes || '—'}
                </td>
                <td className="px-4 py-2 text-gray-500 text-xs">
                  {m.created_at ? new Date(m.created_at).toLocaleString() : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
